// Derived advertising metrics: pure ratio math shared across pages.
//
// The divisor guard is falsy-based: 0, NaN, null or undefined all yield 0 rather than Infinity or
// NaN, so a metric with no denominator renders as 0.
//
// Deliberately not bpm-types' metric registry. `computeRatioAggregation` in
// @blisspointmedia/bpm-types/dist/Metrics is the authoritative formula source (every ratio
// metric's recipe runs through it), but it returns null for a zero or
// non-finite denominator where these return 0 -- null renders as "--", 0 renders as "0", so
// switching is a product decision, not a refactor. It also takes a summed-column datum rather than
// positional scalars, which is the wrong shape for these call sites. If that 0 convention turns
// out to be incidental rather than intended, adopting the registry is the better end state and
// wants its own ticket; until then, extend this file rather than reaching for a ninth convention.

/**
 * Calculate Cost-Per-Click (CPC).
 */
export const calculateCPC = (spend: number, clicks: number): number =>
  clicks ? spend / clicks : 0;

/**
 * Calculate Cost-Per-Thousand Impressions (CPM).
 */
export const calculateCPM = (spend: number, impressions: number): number =>
  impressions ? (spend / impressions) * 1000 : 0;

/**
 * Calculate Cost-Per-X. X is the selected KPI (CPX)
 */
export const calculateCPX = (spend: number, volume: number): number =>
  volume ? spend / volume : 0;

/**
 * Calculate Return on Ad Spend (ROAS)
 */
export const calculateROAS = (revenue: number, spend: number): number =>
  spend ? revenue / spend : 0;

/**
 * Calculate Average Order Value (AOV)
 */
export const calculateAOV = (revenue: number, volume: number): number =>
  volume ? revenue / volume : 0;

/**
 * Calculate Click Through Rate (CTR)
 */
export const calculateCTR = (clicks: number, impressions: number): number =>
  impressions ? clicks / impressions : 0;

/**
 * Calculate Clicks Conversion Rate (CR)
 */
export const calculateCCR = (volume: number, clicks: number): number =>
  clicks ? volume / clicks : 0;

/**
 * Calculate Impressions Conversion Rate (CR)
 */
export const calculateICR = (volume: number, impressions: number): number =>
  impressions ? volume / impressions : 0;
